import { api } from '@mbscan/client-api'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

import { ResultsScreen } from '@/components/screens/results-screen'
import { toPendingResult, toResultViewModel } from '@/features/scan/result-view-model'
import { useScanSession } from '@/features/scan/scan-session-context'
import { useActiveTenant } from '@/features/tenant/tenant-context'

function HistoryPage() {
  const tenant = useActiveTenant()
  const session = useScanSession()
  const navigate = useNavigate()
  const [selectedDate, setSelectedDate] = useState(() => dayjs().format('YYYY-MM-DD'))
  const { data, refetch } = api.useFetchMetalTagScanResultsQuery({
    variables: { date: selectedDate, filter: 'all', take: 200 },
    pollInterval: session.pendingScans.some(({ status }) => status === 'processing') ? 5_000 : 0,
  })
  useEffect(() => {
    if (session.resolvedVersion > 0) {
      void refetch().catch(() => undefined)
    }
  }, [refetch, session.resolvedVersion])
  const results = useMemo(() => {
    const serverResults = data?.metalTagScanResults.items.map(toResultViewModel) ?? []
    const serverClientRequestIds = new Set(
      data?.metalTagScanResults.items.flatMap(
        (entry) => entry.scanLog?.clientRequestId ?? entry.failure?.clientRequestId ?? [],
      ) ?? [],
    )
    const pendingResults = [...session.pendingScans]
      .reverse()
      .filter(({ capturedAt, clientRequestId }) => {
        return dayjs(capturedAt).format('YYYY-MM-DD') === selectedDate && !serverClientRequestIds.has(clientRequestId)
      })
      .map(toPendingResult)
    return [...pendingResults, ...serverResults]
  }, [data, selectedDate, session.pendingScans])

  return (
    <ResultsScreen
      tenant={tenant}
      results={results}
      initialSelectedDate={selectedDate}
      scanCountsByDate={Object.fromEntries(
        data?.metalTagScanResults.dayCounts.map(({ count, date }) => [date, count]) ?? [],
      )}
      onDateChange={setSelectedDate}
      onBack={() => {
        void navigate({ to: '/t/$tenant_id', params: { tenant_id: tenant.id } })
      }}
      onSwitchTenant={() => {
        void navigate({ to: '/' })
      }}
      onScan={() => {
        void navigate({ to: '/t/$tenant_id/millsheet/scan', params: { tenant_id: tenant.id } })
      }}
      onSelect={(result) => {
        if (!String(result.id).startsWith('pending:')) {
          void navigate({
            to: '/t/$tenant_id/millsheet/history/$result_id',
            params: { tenant_id: tenant.id, result_id: String(result.id) },
          })
        }
      }}
    />
  )
}

export const Route = createFileRoute('/t/$tenant_id/millsheet/history/')({ component: HistoryPage })
